import { ChevronDown } from 'lucide-react'

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, NavLink, Outlet } from 'react-router-dom'

import { CompanionControl } from '@/components/pos/companion/CompanionControl'
import { useHeldOrders } from '@/contexts/HeldOrdersContext'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { getOpenShift } from '@/db/repositories/shifts'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { useDiscountCap } from '@/hooks/features/register'
import { cn } from '@/lib/utils'
import { formatMoney, formatTime } from '@/utils'

import { visibleNav } from './nav'

/** `page`: the rail page whose permission the tab follows. */
const TABS = [
  { to: '/', page: '/', label: 'Sale' },
  { to: '/register/orders', page: '/orders', label: 'Orders' },
  { to: '/register/shift', page: '/shift', label: 'Shift' },
]

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <span className="whitespace-nowrap">
    <span className="text-muted-foreground">{label}</span> <span className="font-medium text-ink">{children}</span>
  </span>
)

/** The register (POS.md §18, design "Register" screen): who and where on top, then the shift's Sale / Orders / Shift. */
export const RegisterFrame = () => {
  const register = useRegisterContext()
  const { stage, can } = useSession()
  const sync = useSyncState()
  const cap = useDiscountCap()
  const { held } = useHeldOrders()
  const [showInfo, setShowInfo] = useState(false)
  const allowed = new Set(visibleNav(stage, can).map((n) => n.to))
  const online = stage === 'demo' || (sync.status !== 'offline' && sync.status !== 'error')
  // The back office asked to close this shift (POS.md §18, Terminals page); it arrives with the pull.
  const { data: closeAsked } = useQuery({
    queryKey: ['close-requested', sync.lastSyncedAt],
    queryFn: async () => Boolean((await getOpenShift())?.close_requested_at),
    enabled: stage === 'ready',
  })

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-card px-4 py-2.5 text-xs sm:gap-x-4">
        <span className="text-heading-md text-ink">
          IWS POS
          {register.terminal_no !== undefined && <span className="ml-1.5 text-xs font-normal text-muted-foreground">· T{register.terminal_no}</span>}
        </span>
        {/* A phone gets one line; who and where open on tap. */}
        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          aria-expanded={showInfo}
          className="flex min-w-0 items-center gap-1 text-muted-foreground sm:hidden"
        >
          <span className="truncate">{register.branch ?? register.cashier}</span>
          <ChevronDown className={cn('size-3.5 shrink-0 transition-transform', showInfo && 'rotate-180')} />
        </button>
        <div className={cn('order-last w-full flex-wrap items-center gap-x-4 gap-y-1 sm:order-none sm:flex sm:w-auto', showInfo ? 'flex' : 'hidden')}>
          {register.branch && <Field label="Branch">{register.branch}</Field>}
          <span className="whitespace-nowrap">
            <span className="text-muted-foreground">Cashier</span> <span className="font-medium text-ink">{register.cashier}</span>
            {cap && (
              <span className="ml-1 text-muted-foreground">
                max discount {cap.type === 'percentage' ? `${cap.value}%` : formatMoney(cap.value)}
              </span>
            )}
          </span>
          <Field label="Shift">
            {register.opened_at ? `${register.shift_no ? `${register.shift_no} · ` : ''}${register.name} · since ${formatTime(register.opened_at)}` : 'not open'}
          </Field>
        </div>
        <span className="ml-auto flex items-center gap-2">
          <CompanionControl />
          <Link to="/" state={{ held: true }} className="rounded-sm border border-border px-3 py-1 font-medium text-ink hover:bg-fill">
            <span className="hidden sm:inline">Held carts · </span>
            <span className="sm:hidden">Held · </span>
            {held.length}
          </Link>
          <span
            title={online ? 'Online' : 'Offline'}
            className="flex items-center gap-1.5 rounded-full border border-border px-2 py-1 font-medium text-ink sm:px-3"
          >
            <span className={cn('size-2 rounded-full', online ? 'bg-success' : 'bg-grey-500')} />
            <span className="hidden sm:inline">{online ? 'Online' : 'Offline'}</span>
          </span>
        </span>
      </header>
      <nav className="flex gap-1 border-b border-border bg-card px-4" aria-label="Register">
        {TABS.filter((t) => allowed.has(t.page)).map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === '/'}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'border-ink text-ink' : 'border-transparent text-muted-foreground hover:text-ink'
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      {closeAsked && (
        <Link to="/register/shift" className="block bg-cta-lighter px-4 py-2 text-sm text-cta-dark">
          The back office asked to close this shift. Count the drawer and close it →
        </Link>
      )}
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
